import { Route, createBrowserRouter, createRoutesFromElements, RouterProvider, LoaderFunctionArgs, redirect } from 'react-router-dom';
import { Content } from './components/Content';
import { NewsItem } from './pages/NewsItem';
import { News } from './pages/News';
import { Home } from './pages/Home';

import { NotFound } from './pages/NotFound';
import { redirectWithSearch } from './redirectWithSearch';
import { Download } from './pages/Download';
import { Changelog } from './pages/Changelog';
import { HostAuth } from './pages/HostAuth';
import { Privacy } from './pages/Privacy';
import { TermsOfService } from './pages/Terms';
import { EULA } from './pages/EULA';
import { FAQs } from './pages/FAQs';
import { AboutUs } from './pages/About';
import { Discord } from './pages/Discord';
import { ElectronAuth } from './pages/ElectronAuth';
import { useUser } from '@clerk/clerk-react';
import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { Dev } from './pages/Dev';
import { DiscordBot } from './pages/DiscordBot';
import { ObsQr } from './pages/ObsQr';
import { Affiliate } from './pages/Affiliate';
import { Ios } from './pages/Ios';
import { ProfilePage } from './pages/Profile';
import { Joiner } from './pages/Joiner';
import { Capture } from './player/Capture';
import { MobileGamepadPage } from './player/MobileGamepad';
import { ShowDev } from './components/dev/ShowDev';
import { Fullscreen } from './player/Fullscreen';
import { Toaster } from '@playcastdotio/Shadcn';
import { DevOverlay } from './components/dev/DevOverlay';
import { PlayerConnectionWrapper } from './components/PlayerConnectionWrapper';
import { IdentityPlatform } from '@playcastdotio/CoreTypes';
import { PavilionContext } from './contexts/PavilionContext';
import { AffiliateLanding } from './pages/AffiliateLanding';
import { SponsoredAffiliateLanding } from './pages/SponsoredAffiliateLanding';
import { SoftKickHost } from './pages/SoftKickHost';
import { PlaycastAnalyticsPageViewed, sendAnalytics } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../api';
import '../metrics';
import { DevDisconnects } from './pages/DevDisconnects';
import { DevInvitations } from './pages/DevInvitations';
import { Quickstart } from './pages/QuickStart';
import { Login } from './pages/Login';
import CONFIG from '@playcastdotio/GlobalConfig';
import { authStore } from '../api/stores/globalStores';
import { StfAttribution } from './pages/StfAttribution';
import { GameGallery } from './pages/GameGallery';
import { NonsoleMode } from './pages/NonsoleMode';
import { ServicesAgreement } from './pages/ServicesAgreement';
import { Hosts } from './pages/Hosts';
import { Developers } from './pages/Developers';
import { RWBYNewsAnnouncement } from './pages/RWBYNewsAnnouncement';

// Redirect player pages to home page (respects environment: local/dev/dev2/prerelease/release)
const redirectToHome = () => {
  const isLocal = CONFIG.VERSION.endsWith('-local');
  const branch = CONFIG.VERSION.split('-')[1]; // e.g., "1.15.0-dev" → "dev"

  let homeUrl = '';

  if (isLocal) {
    // Local: http://localhost:4204/ or http://pavilion.localhost:4204/
    homeUrl = 'http://localhost:4204/';
  } else {
    // Build URL based on branch
    switch (branch) {
      case 'dev':
        homeUrl = 'https://dev.marketing.playcast.io/';
        break;
      case 'dev2':
        homeUrl = 'https://dev2.marketing.playcast.io/';
        break;
      case 'prerelease':
        homeUrl = 'https://prerelease.marketing.playcast.io/';
        break;
      default:
        // Release: https://playcast.io/ or https://pavilion.playcast.io/
        homeUrl = 'https://playcast.io/';
        break;
    }
  }

  window.location.href = homeUrl;
  return null;
};

// Redirect /devs/onboarding -> Google Forms onboarding URL
// Note: External destination, so use a full redirect response.
const redirectDevsOnboarding = () => {
  return redirect('https://forms.gle/iA5XyqdFF9sQ5cRQ8');
};

const redirectQuickstartToDemo = ({ params, request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const queryParams = url.search;
  const quickstart = params.quickstart || '';
  const demoUrl = `${CONFIG.DEMO_BASE_URL}q/${quickstart}${queryParams}`;

  return redirect(demoUrl);
};

// Define router creation function so we can create it within the component
// to have access to the isPavilionMode prop
const createAppRouter = () => {
  return createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route path='/' element={<Content />}>
          <Route index element={<Home />} />
          {/* MARKETING ROUTES */}
          <Route path='devs/onboarding/*' loader={redirectDevsOnboarding} />
          <Route path='/news'>
            <Route index element={<News />} />
            <Route path='rwby-grimm-eclipse-launch' element={<RWBYNewsAnnouncement />} />
            <Route path=':slug' element={<NewsItem />} />
          </Route>
          <Route path='/a'>
            <Route path=':affiliate' element={<Affiliate />} />
          </Route>
          <Route path='/affiliate-welcome' loader={redirectToHome} element={<AffiliateLanding />}>
            <Route path=':affiliate' loader={redirectToHome} element={<AffiliateLanding />} />
          </Route>
          <Route path='/sponsored-affiliate-welcome'>
            <Route path=':affiliate' loader={redirectToHome} element={<SponsoredAffiliateLanding />} />
          </Route>
          <Route path='download/'>
            <Route path=':affiliate' element={<Download />} />
          </Route>
          <Route path='host-auth' element={<HostAuth />} />
          <Route path='changelog' element={<Changelog />} />
          <Route path='privacy-policy' element={<Privacy />} />
          <Route path='terms-of-service' element={<TermsOfService />} />
          <Route path='terms-and-conditions' loader={redirectWithSearch('/terms-of-service')} />
          <Route path='eula' element={<EULA />} />
          <Route path='discord' element={<Discord />} />
          <Route path='discord-bot' element={<DiscordBot />} />
          <Route path='faqs' element={<FAQs />} />
          <Route path='about' element={<AboutUs />} />
          <Route path='host-login' element={<ElectronAuth />} />
          <Route path='host-control' element={<SoftKickHost />} />
          <Route path='host/helldivers-ii' loader={redirectWithSearch('/hosts')} />
          <Route path='host/outbreak' loader={redirectWithSearch('/hosts')} />
          <Route path='host/steam-card' loader={redirectWithSearch('/hosts')} />
          <Route path='host/steam-card/terms-and-conditions' loader={redirectWithSearch('/terms-of-service')} />
          <Route path='login' element={<Login />} />
          <Route path='stf-attribution' element={<StfAttribution />} />
          <Route path='gamegallery' element={<GameGallery />} />
          <Route path='nonsole-mode' element={<NonsoleMode />} />
          <Route path='services-agreement' element={<ServicesAgreement />} />
          <Route path='hosts' element={<Hosts />} />
          <Route path='hosts/:audience' element={<Hosts />} />
          <Route path='developers' element={<Developers />} />
          <Route path='*' id='not-found' element={<NotFound />} />

          {/* PLAYER ROUTES */}
          <Route element={<PlayerConnectionWrapper />}>
            <Route path='ios' element={<Ios />} />
            <Route path='j/:joinId' element={<Joiner platform={IdentityPlatform.CLERK} />} />
            <Route path='jd/:joinId' element={<Joiner platform={IdentityPlatform.DISCORD} />} />
            <Route path='jt/:joinId' element={<Joiner platform={IdentityPlatform.TWITCH} />} />
            {/* PROFILE PAGES - Redirected to home page */}
            <Route path='p/:alias' loader={redirectToHome} element={<ProfilePage platform={IdentityPlatform.CLERK} />} />
            <Route path='pt/:alias' loader={redirectToHome} element={<ProfilePage platform={IdentityPlatform.TWITCH} />} />
            <Route path='pd/:alias' loader={redirectToHome} element={<ProfilePage platform={IdentityPlatform.DISCORD} />} />
            <Route path='q/:quickstart' loader={redirectQuickstartToDemo} element={<Quickstart />} />
          </Route>
        </Route>
        <Route path='qr/:hash' element={<ObsQr />} />

        {/* DEV ROUTES */}
        <Route path='dev'>
          <Route index element={<Dev />} />
          <Route path='capture' element={<Capture />} />
          <Route path='mobile-gamepad' element={<MobileGamepadPage />} />
          <Route path='fullscreen' element={<Fullscreen />} />
          <Route path='disconnects' element={<DevDisconnects />} />
          <Route path='invitations' element={<DevInvitations />} />
        </Route>
        <Route path='showdev' element={<ShowDev />} />
      </>,
    ),
  );
};

export const App = ({ isPavilionMode }: { isPavilionMode: boolean }) => {
  const { user } = useUser();
  const router = createAppRouter();

  useEffect(() => {
    if (user) {
      const username = user?.username ?? user?.fullName ?? undefined;
      const email = user?.primaryEmailAddress?.emailAddress;
      const id = user?.id;
      if (email && id) {
        if (Sentry.isInitialized()) {
          Sentry.setUser({
            username,
            email,
            id,
            ip_address: '{{auto}}',
          });
          console.log('Sentry.setUser');
        }
        localStorage.setItem('clerkUserID', id ?? '');
      }
    } else {
      // Clear user data on logout
      if (Sentry.isInitialized()) {
        Sentry.setUser(null);
        console.log('Sentry.setUser cleared');
      }
      localStorage.removeItem('clerkUserID');
      localStorage.removeItem('name');
      localStorage.removeItem('playcast.peer.disconnectHistory');
      localStorage.removeItem('playcast-player-id');
    }
  }, [user]);

  useEffect(() => {
    document.documentElement.className = 'theme-midnight';
  }, []);

  const lastPageRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const path = location.pathname;
    if (lastPageRef.current === path) {
      return;
    }

    lastPageRef.current = path;

    sendAnalytics({
      name: 'Page Viewed',
      role: 'host',
      persistentId: getPersistentPlayerId(),
      playerAuthId: authStore.currentState.playerAuthId,
      page: path,
    } as PlaycastAnalyticsPageViewed);
  }, [location]);

  return (
    <PavilionContext.Provider value={{ isPavilionMode }}>
      <style>{`
        [data-sonner-toaster][data-x-position="center"] {
          top: 9px !important;
          margin: 0 !important;
        }
        [data-sonner-toaster][data-x-position="center"] ol {
          margin: 0 auto !important;
        }
      `}</style>
      <Toaster richColors position='top-center' />
      <DevOverlay />
      <RouterProvider router={router} />
    </PavilionContext.Provider>
  );
};

